/**
 * WebSocket service for Team Chat.
 * Handles real-time messaging, typing indicators, and presence.
 */

import { authService } from '../lib/auth';
import type {
  WebSocketEvent,
  WebSocketCommand,
  TypingPayload,
  PresencePayload,
  ReactionUpdatePayload,
  Message,
  MessageDeletedPayload,
} from '../types/chat';

type EventHandler = (data: unknown) => void;
type EventMap = Map<string, Set<EventHandler>>;

class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  // Bumped by disconnect() so an in-flight token refresh doesn't reconnect.
  private connectGeneration = 0;
  private messageQueue: WebSocketCommand[] = [];
  private pingInterval: number | null = null;
  private events: EventMap = new Map();
  private authErrorDetected = false;
  private joinedChannels: Set<string> = new Set();

  // Event emitter methods
  on(event: string, handler: EventHandler): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)?.add(handler);
  }

  off(event: string, handler: EventHandler): void {
    this.events.get(event)?.delete(handler);
  }

  emit(event: string, data?: unknown): void {
    this.events.get(event)?.forEach((handler) => handler(data));
  }

  connect(organizationId?: string, skipTokenRefresh = false): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    if (this.authErrorDetected) {
      console.warn('ChatWebSocket: Auth error detected, not attempting reconnect');
      this.emit('auth_error');
      return;
    }

    // The token is only checked at the handshake, so a reconnect after the
    // access token expires would be rejected on every retry. Refresh first.
    if (!skipTokenRefresh && authService.isAccessTokenExpiring()) {
      this.isConnecting = true;
      const generation = this.connectGeneration;
      void authService.ensureFreshAccessToken().finally(() => {
        if (generation !== this.connectGeneration) {
          return; // disconnect() was called while refreshing
        }
        this.isConnecting = false;
        this.connect(organizationId, true);
      });
      return;
    }

    this.isConnecting = true;

    // Get JWT token for authentication
    let token: string | null = null;
    try {
      token = authService.getAccessToken();
    } catch (error) {
      console.warn('ChatWebSocket: Could not get access token:', error);
    }

    // Build WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const path = organizationId
      ? `/ws/teamchat/${organizationId}/`
      : '/ws/teamchat/';

    const url = `${protocol}//${host}${path}`;
    const subprotocols = token ? ['access_token', token] : undefined;

    try {
      this.ws = subprotocols ? new WebSocket(url, subprotocols) : new WebSocket(url);

      this.ws.onopen = () => {
        console.log('ChatWebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.authErrorDetected = false;
        this.emit('connected');

        // Rejoin channels we were in
        this.joinedChannels.forEach((channelId) => {
          this.send({ action: 'join_channel', channel_id: channelId });
        });

        // Flush message queue
        this.flushMessageQueue();

        // Start ping interval
        this.startPingInterval();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketEvent = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('ChatWebSocket: Failed to parse message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('ChatWebSocket error:', error);
        this.isConnecting = false;
        this.emit('error', error);
      };

      this.ws.onclose = (event) => {
        console.log('ChatWebSocket disconnected', event.code, event.reason);
        this.isConnecting = false;
        this.ws = null;
        this.stopPingInterval();
        this.emit('disconnected');

        // Check for auth errors
        const isAuthError =
          event.code === 1008 ||
          event.code === 1002 ||
          (event.reason &&
            (event.reason.toLowerCase().includes('unauthorized') ||
              event.reason.toLowerCase().includes('forbidden') ||
              event.reason.toLowerCase().includes('authentication') ||
              event.reason.toLowerCase().includes('permission')));

        if (isAuthError) {
          console.error('ChatWebSocket: Authentication error, stopping reconnection');
          this.authErrorDetected = true;
          this.emit('auth_error', { code: event.code, reason: event.reason });
          return;
        }

        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && event.code !== 1001) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('ChatWebSocket: Failed to create:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private handleMessage(event: WebSocketEvent): void {
    // Emit specific events based on type
    this.emit(event.type, event.payload);

    // Also emit a general message event
    this.emit('message', event);

    // Handle specific message types
    switch (event.type) {
      case 'new_message':
        this.emit('new_message', event.payload as Message);
        break;
      case 'message_updated':
        this.emit('message_updated', event.payload as Message);
        break;
      case 'message_deleted':
        this.emit('message_deleted', event.payload as MessageDeletedPayload);
        break;
      case 'typing':
        this.emit('typing', event.payload as TypingPayload);
        break;
      case 'presence':
        this.emit('presence', event.payload as PresencePayload);
        break;
      case 'reaction_update':
        this.emit('reaction_update', event.payload as ReactionUpdatePayload);
        break;
      case 'channel_joined':
        this.emit('channel_joined', event.payload);
        break;
      case 'channel_left':
        this.emit('channel_left', event.payload);
        break;
      case 'pong':
        // Heartbeat response, no action needed
        break;
    }
  }

  private scheduleReconnect(): void {
    if (
      this.reconnectTimer ||
      this.reconnectAttempts >= this.maxReconnectAttempts ||
      this.authErrorDetected
    ) {
      return;
    }

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      30000
    );

    console.log(
      `ChatWebSocket: Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`
    );

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ action: 'ping', timestamp: Date.now() });
      }
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      window.clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift();
      if (message) {
        this.ws.send(JSON.stringify(message));
      }
    }
  }

  send(command: WebSocketCommand): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(command));
    } else {
      this.messageQueue.push(command);
      if (!this.isConnecting) {
        this.connect();
      }
    }
  }

  // Channel actions
  joinChannel(channelId: string): void {
    this.joinedChannels.add(channelId);
    this.send({ action: 'join_channel', channel_id: channelId });
  }

  leaveChannel(channelId: string): void {
    this.joinedChannels.delete(channelId);
    this.send({ action: 'leave_channel', channel_id: channelId });
  }

  // Typing indicators
  startTyping(channelId: string): void {
    this.send({ action: 'typing_start', channel_id: channelId });
  }

  stopTyping(channelId: string): void {
    this.send({ action: 'typing_stop', channel_id: channelId });
  }

  // Mark as read
  markRead(channelId: string, messageId?: string): void {
    this.send({ action: 'mark_read', channel_id: channelId, message_id: messageId });
  }

  disconnect(): void {
    this.connectGeneration++;
    this.stopPingInterval();

    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.messageQueue = [];
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.authErrorDetected = false;
    this.joinedChannels.clear();
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getJoinedChannels(): string[] {
    return Array.from(this.joinedChannels);
  }
}

// Export singleton instance
const chatWebsocketService = new ChatWebSocketService();
export default chatWebsocketService;
